import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { LoadingState } from "~/common/components";
import {
  hasAdminAccess,
  hasConsultationAccess,
  hasGeneralAccess,
} from "~/common/utils/checkUserPermissions";
import {
  ConsultationLayout,
  Layout,
  PatientProfileLayout,
} from "~/providers/layout";
import {
  ConsultationLinksScreen,
  ConsultationOptionsScreen,
  ConsultationRequestsScreen,
  GeneralScreen,
  Home,
  InternalErrorScreen,
  LeadPatientActivityFeed,
  LeadPatientAssessmentAndPlan,
  LeadPatientConsultationReport,
  LeadPatientInformation,
  LeadPatientPreview,
  LeadPatientProgressNote,
  LeadPatientRawResponses,
  NotFoundScreen,
  OngoingPatientAssessmentAndPlan,
  OngoingPatientConsultationReport,
  OngoingPatientInformation,
  OngoingPatientPreview,
  OngoingPatientProgressNote,
  OngoingPatientRawResponses,
  PatientActivityFeed,
  PatientMedication,
  PatientOrders,
  PatientSessions,
} from "~/providers/screens";
import { CarePlans, PrescriptionRequest, ProviderProfile } from "../components";
import { SessionBillingNote } from "../components/patientProfile/sessions/SessionBillingNote";
import { SessionLayout } from "../components/patientProfile/sessions/SessionLayout";
import { SessionSoapNote } from "../components/patientProfile/sessions/SessionSoapNote";
import { SessionTranscript } from "../components/patientProfile/sessions/SessionTranscript";
import { useGetCurrentTeamMember } from "../hooks";
import { CurrentTeamMemberContext } from "../hooks/useCurrentTeamMemberContext";
import { SettingsLayout } from "../layout/SettingsLayout";
import { ToolsLayout } from "../layout/ToolsLayout";
import { PatientSearch } from "../screens/PatientSearch";
import { SuccessAuthScreen } from "../screens/SuccessAuthScreen";
import { IntegrationsScreen } from "../screens/tools/IntegrationsScreen";
import { VerifyPhone } from "../screens/VerifyPhone";
import { getHomeRouteByPermissions } from "../utils";
import { ROUTES } from "./routes";
import { VerifiedRoutes } from "./VerifiedRoutes";

interface AuthRouterProps {
  previousLocation?: Location;
}
export const AuthenticatedRouter = ({ previousLocation }: AuthRouterProps) => {
  const location = useLocation();

  const { data: teamMember } = useGetCurrentTeamMember();

  if (!teamMember) return <LoadingState className="h-screen" />;

  return (
    <CurrentTeamMemberContext.Provider value={teamMember}>
      <Routes location={previousLocation ?? location}>
        <Route element={<VerifyPhone />} path={ROUTES.verification} />
        <Route element={<VerifiedRoutes />}>
          <Route
            element={
              <Navigate
                to={getHomeRouteByPermissions(teamMember.user_permissions)}
              />
            }
            path="*"
          />
          <Route path={"*"} element={<NotFoundScreen />} />
          <Route
            path={ROUTES.internalError}
            element={<InternalErrorScreen />}
          />
          <Route element={<SuccessAuthScreen />} path={ROUTES.successAuth} />
          {hasAdminAccess(teamMember.user_permissions) && (
            <Route element={<Layout />}>
              <Route element={<ToolsLayout />}>
                <Route element={<GeneralScreen />} path={ROUTES.toolsGeneral} />
                <Route
                  element={<ConsultationOptionsScreen />}
                  path={ROUTES.toolsConsultationOptions}
                />
                <Route
                  element={<ConsultationLinksScreen />}
                  path={ROUTES.toolsConsultationLinks}
                />
                <Route
                  element={<IntegrationsScreen />}
                  path={ROUTES.toolsIntegrations}
                />
              </Route>
            </Route>
          )}
          {hasConsultationAccess(teamMember.user_permissions) && (
            <Route>
              <Route element={<Layout />}>
                <Route
                  element={<ConsultationRequestsScreen />}
                  path={ROUTES.consultationRequests}
                />
                <Route element={<PatientProfileLayout />}>
                  <Route element={<ConsultationLayout />}>
                    <Route
                      path={ROUTES.consultationPreview}
                      element={<LeadPatientPreview />}
                    />
                    <Route
                      path={ROUTES.consultationResponses}
                      element={<LeadPatientRawResponses />}
                    />
                    <Route
                      path={ROUTES.consultationComplete}
                      element={<LeadPatientAssessmentAndPlan />}
                    />
                    <Route
                      path={ROUTES.leadPatientConsultationReport}
                      element={<LeadPatientConsultationReport />}
                    />
                    <Route
                      path={ROUTES.leadPatientProgressNote}
                      element={<LeadPatientProgressNote />}
                    />
                    <Route
                      path={ROUTES.patientPreview}
                      element={<OngoingPatientPreview />}
                    />
                    <Route
                      path={ROUTES.patientResponses}
                      element={<OngoingPatientRawResponses />}
                    />
                    <Route
                      path={ROUTES.patientComplete}
                      element={<OngoingPatientAssessmentAndPlan />}
                    />
                    <Route
                      path={ROUTES.ongoingPatientConsultationReport}
                      element={<OngoingPatientConsultationReport />}
                    />
                    <Route
                      path={ROUTES.ongoingPatientProgressNote}
                      element={<OngoingPatientProgressNote />}
                    />
                  </Route>
                  <Route
                    element={<PatientActivityFeed />}
                    path={ROUTES.activePatientActivityFeed}
                  />
                  <Route
                    element={<LeadPatientActivityFeed />}
                    path={ROUTES.leadPatientActivityFeed}
                  />
                  <Route
                    element={<OngoingPatientInformation />}
                    path={ROUTES.patientInfo}
                  />
                  <Route
                    element={<LeadPatientInformation />}
                    path={ROUTES.patientConsultationInfo}
                  />
                  <Route element={<PatientSessions />} path={ROUTES.sessions} />
                  <Route
                    path={ROUTES.sessionDetail}
                    element={<SessionLayout />}
                  >
                    <Route
                      index
                      element={<Navigate to="transcript" replace />}
                    />
                    <Route path="transcript" element={<SessionTranscript />} />
                    <Route path="soap" element={<SessionSoapNote />} />
                    <Route path="billing" element={<SessionBillingNote />} />
                  </Route>
                </Route>
                <Route element={<SettingsLayout />}>
                  <Route element={<ProviderProfile />} path={ROUTES.profile} />
                </Route>
                <Route
                  element={<PatientSearch />}
                  path={ROUTES.patientSearch}
                />
              </Route>
            </Route>
          )}
          {hasGeneralAccess(teamMember.user_permissions) && (
            <>
              <Route element={<Layout />}>
                <Route element={<Home />} path={ROUTES.home} />
                <Route
                  element={<PrescriptionRequest />}
                  path={ROUTES.prescriptionRequest}
                />
                <Route element={<PatientProfileLayout />}>
                  <Route
                    element={<PatientMedication />}
                    path={ROUTES.medication}
                  />
                  <Route element={<PatientOrders />} path={ROUTES.orders} />
                  <Route element={<PatientSessions />} path={ROUTES.sessions} />
                  <Route
                    path={ROUTES.sessionDetail}
                    element={<SessionLayout />}
                  >
                    <Route
                      index
                      element={<Navigate to="transcript" replace />}
                    />
                    <Route path="transcript" element={<SessionTranscript />} />
                    <Route path="soap" element={<SessionSoapNote />} />
                    <Route path="billing" element={<SessionBillingNote />} />
                  </Route>
                </Route>
                <Route element={<SettingsLayout />}>
                  <Route element={<ProviderProfile />} path={ROUTES.profile} />
                  <Route element={<CarePlans />} path={ROUTES.carePlans} />
                </Route>
                <Route
                  element={<PatientSearch />}
                  path={ROUTES.patientSearch}
                />
              </Route>
            </>
          )}
        </Route>
      </Routes>
    </CurrentTeamMemberContext.Provider>
  );
};
